<template>
  <div v-if="isLoading" class="loading-container">
    <img :src="require('@/assets/10.png')" alt="Logo" class="loading-logo" />
  </div>
  <div v-else>
    <div class="slide">
      <div class="bg-img">
        <div class="slide-content">
          <div>
            <h2 class="slide-header">{{ $t('message.technicalTracks') }}</h2>
          </div>
          <div>
            <h5 class="slide-h5">{{ $t('message.home') }}</h5>
            <b-icon :icon="isRTL ? 'chevron-double-left' : 'chevron-double-right'" class="icon-slider"></b-icon>
            <h5 class="slide-h5"> {{ $t('message.technicalTracks') }}</h5>
          </div>
        </div>
      </div>
    </div>
    
    <div class="container custom-container-tracks mt-5">
      <!-- Map Container -->
      <div class="map-container-wrapper">
        <div class="map-container">
          <l-map 
            ref="map"
            :zoom="zoom" 
            :center="center"
            style="height: 500px; width: 100%;">
            <l-tile-layer :url="mapUrl"></l-tile-layer>
            <l-polyline
              v-if="trackPoints.length > 0"
              :lat-lngs="trackPoints"
              color="#3C89CE"
              :weight="5"
              :opacity="0.8">
            </l-polyline>
            <l-marker
              v-if="trackPoints.length > 0"
              :lat-lng="lastPosition">
            </l-marker>
          </l-map>
        </div>
      </div>

      <!-- Technician Info Panel -->
      <div class="technician-info-panel">
        <div class="tech-info-content">
          <b-icon icon="calendar-range" class="icon-calendar"></b-icon>
          <div class="tech-main-info">
            <h4 class="tech-name">{{ technicianName }}</h4>
            <div class="tech-date-input">
              <input 
                type="date" 
                v-model="selectedDate" 
                @change="fetchTracks"
                class="date-input"
              />
            </div>
          </div>
          <div class="tech-distance">
            <b-icon icon="arrow-left-right" class="icon-distance"></b-icon>
            <div>
              <p class="distance-label">{{ $t('message.totalDistance') }}</p>
              <p class="distance-value">{{ totalDistance.toFixed(2) }} km</p>
            </div>
          </div>
          <div class="tech-avatar-container">
            <img 
              :src="technicianImage || require('../assets/victor.png')" 
              alt="Technician" 
              @error="imageError"
              class="technician-avatar"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../axios-config';
import { LMap, LTileLayer, LPolyline, LMarker } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
  name: 'TechnicalTracks',
  components: {
    LMap,
    LTileLayer,
    LPolyline,
    LMarker
  },
  props: ['id'],
  data() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const defaultDate = `${year}-${month}-${day}`;

    return {
      isLoading: true,
      technicianName: '',
      technicianImage: '',
      selectedDate: defaultDate,
      locations: [],
      trackPoints: [],
      mapUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 13,
      center: [31.2001, 29.9187], // Default to Cairo
      totalDistance: 0
    };
  },
  computed: {
    isRTL() {
      return this.$i18n.locale === 'ar';
    },
    lastPosition() {
      if (this.trackPoints.length > 0) {
        const last = this.trackPoints[this.trackPoints.length - 1];
        return [last[0], last[1]];
      }
      return this.center;
    }
  },
  created() {
    this.fetchTechnicianInfo();
    this.fetchTracks();
  },
  methods: {
    imageError(event) {
      event.target.src = require('../assets/victor.png');
    },
    
    async fetchTechnicianInfo() {
      try {
        const formData = new FormData();
        formData.append('offset', 0);
        formData.append('company_id', this.$store.getters.getUser.company_id);
        const response = await axiosInstance.post('select_employees_company.php', formData);
        
        if (response.data.status === "true") {
          const employee = response.data.output.find(emp => emp.id == this.id);
          if (employee) {
            this.technicianName = employee.name;
            this.technicianImage = employee.image;
          }
        }
      } catch (error) {
        console.error('Error fetching technician info:', error);
      }
    },
    
    async fetchTracks() {
      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append('technical_id', this.id);
        formData.append('date', this.selectedDate);
        
        const response = await axiosInstance.post('technical/select_technical_location.php', formData);
        
        console.log('Tracks Response:', response.data);
        
        if (response.data.status === "true" && response.data.output && response.data.output.length > 0) {
          this.locations = response.data.output;
          this.trackPoints = this.locations.map(loc => [parseFloat(loc.latitude), parseFloat(loc.longitude)]);
          
          // Calculate total distance
          this.calculateDistance();
          
          // Set map center to first point
          if (this.trackPoints.length > 0) {
            this.center = this.trackPoints[0];
          }
          
          // Update marker position to last point
          this.$nextTick(() => {
            if (this.$refs.map) {
              this.$refs.map.mapObject.setView(this.trackPoints[0], this.zoom);
            }
          });
        } else {
          this.locations = [];
          this.trackPoints = [];
          this.totalDistance = 0;
          this.$izitoast.info({
            title: 'Info',
            message: 'No tracks found for this date',
            position: 'bottomLeft',
            timeout: 5000,
          });
        }
        
        this.isLoading = false;
      } catch (error) {
        console.error('Error fetching tracks:', error);
        this.$izitoast.error({
          title: 'Error',
          message: 'An error occurred while trying to get tracks',
          position: 'bottomLeft',
          timeout: 5000,
        });
        this.isLoading = false;
      }
    },
    
    calculateDistance() {
      this.totalDistance = 0;
      for (let i = 1; i < this.trackPoints.length; i++) {
        const prev = this.trackPoints[i - 1];
        const curr = this.trackPoints[i];
        const distance = this.getDistanceFromLatLonInKm(
          prev[0], prev[1],
          curr[0], curr[1]
        );
        this.totalDistance += distance;
      }
    },
    
    getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
      const R = 6371; // Radius of the earth in km
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const d = R * c; // Distance in km
      return d;
    },
    
    deg2rad(deg) {
      return deg * (Math.PI/180);
    }
  }
}
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.loading-logo {
  width: 200px;
  height: 200px;
}

.map-container-wrapper {
  margin-bottom: 20px;
}

.map-container {
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.technician-info-panel {
  background: #fff;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.tech-info-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.icon-calendar {
  width: 40px;
  height: 40px;
  color: #3C89CE;
  background: linear-gradient(135deg, #3C89CE 0%, #2274BD 100%);
  border-radius: 50%;
  padding: 10px;
  flex-shrink: 0;
}

.tech-main-info {
  flex: 1;
  min-width: 200px;
}

.tech-name {
  color: #2B5FA8;
  font-family: 'Somar-Bold';
  font-size: 24px;
  margin-bottom: 15px;
  margin-left: 15px;
}

.tech-date-input {
  margin-left: 15px;
}

.date-input {
  border: 2px solid #D1E4FF;
  border-radius: 10px;
  padding: 10px 15px;
  font-size: 16px;
  font-family: 'Somar-Medium';
  color: #45566D;
  background-color: #f8f9fa;
  transition: all 0.3s;
  width: 100%;
  max-width: 250px;
}

.date-input:focus {
  outline: none;
  border-color: #3C89CE;
  background-color: #fff;
}

.tech-distance {
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(135deg, #FFCA1F 0%, #FFD44A 100%);
  padding: 15px 25px;
  border-radius: 12px;
  min-width: 180px;
}

.icon-distance {
  width: 35px;
  height: 35px;
  color: #fff;
  flex-shrink: 0;
}

.distance-label {
  margin: 0;
  color: #fff;
  font-family: 'Somar-Bold';
  font-size: 12px;
  opacity: 0.9;
}

.distance-value {
  margin: 0;
  color: #fff;
  font-family: 'Somar-Bold';
  font-size: 22px;
}

.tech-avatar-container {
  flex-shrink: 0;
}

.technician-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  border: 4px solid #3C89CE;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(60, 137, 206, 0.3);
}

@media (max-width: 768px) {
  .tech-info-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .tech-name {
    margin-left: 0;
  }
  
  .tech-date-input {
    margin-left: 0;
  }
  
  .technician-avatar {
    width: 70px;
    height: 70px;
  }
  
  .tech-distance {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>

